import { Fragment, ReactElement } from 'react';
import { Tooltip } from '@client/common/components/Tooltip';
import { useTranslate } from '@client/i18n/hooks';
import { Icon, ICON_NAME, ICON_SIZE } from '@lib/components/Icon';
import { QUANTITY_PER_SALES_UNIT } from 'b2b-common/core/product/api/Product.types';
import { Props } from './';
import './ProductSpecification.scss';

const mapAttributes = (attributes: { id: string, name: string, values: any[] }[]) => attributes.map((attribute) => (
  <tr key={attribute.id}>
    <td className='cds-Table-Cell--SpecsAttribute'>{attribute.name}</td>
    <td>
      <ul>
        {attribute.values.map((value, key) => <li key={key}>{value}</li>)}
      </ul>
    </td>
  </tr>
));

export const ProductSpecification = ({ specification, packaging }: Props): ReactElement => {
  const t = useTranslate();

  const attributeGroups = specification.map((group, idx) => (
    <Fragment key={idx}>
      <tr key={`${group.name}-${idx}`}>
        <th colSpan={2}>{group.name}</th>
      </tr>
      {mapAttributes(group.attributes)}
    </Fragment>
  ));

  if (packaging) {
    const { quantityPerSalesUnit, quantityPerSalesUnitUnit, containerQuantitySuggestion } = packaging;
    const translations = {
      [QUANTITY_PER_SALES_UNIT.PCK]: {
        packingUnit: t('{quantityPerSalesUnit} pack', { quantityPerSalesUnit }),
        packingUnitPlural: t('{quantityPerSalesUnit} packs', { quantityPerSalesUnit }),
        umpack: t('{containerQuantitySuggestion} pack of {quantityPerSalesUnit}', {
          quantityPerSalesUnit,
          containerQuantitySuggestion,
        }),
        umpackPlural: t('{containerQuantitySuggestion} packs of {quantityPerSalesUnit}', {
          quantityPerSalesUnit,
          containerQuantitySuggestion,
        }),
      },
      [QUANTITY_PER_SALES_UNIT.STK]: {
        packingUnit: t('{quantityPerSalesUnit} piece', { quantityPerSalesUnit }),
        packingUnitPlural: t('{quantityPerSalesUnit} pieces', { quantityPerSalesUnit }),
        umpack: t('{containerQuantitySuggestion} piece of {quantityPerSalesUnit}', {
          quantityPerSalesUnit,
          containerQuantitySuggestion,
        }),
        umpackPlural: t('{containerQuantitySuggestion} pieces of {quantityPerSalesUnit}', {
          quantityPerSalesUnit,
          containerQuantitySuggestion,
        }),
      },
    };

    const text = translations[quantityPerSalesUnitUnit];

    attributeGroups.unshift(
      <Fragment key='packing'>
        <tr key='packing-title'>
          <th colSpan={2}>{t('Bulk packaging')}</th>
        </tr>

        <tr key='packing-unit'>
          <td className='cds-Table-Cell--SpecsAttribute'>{t('Packing Unit')}</td>
          <td>
            <ul>
              <li>{quantityPerSalesUnit > 1 ? text.packingUnitPlural : text.packingUnit}</li>
            </ul>
          </td>
        </tr>

        <tr key='umpack'>
          <td className='cds-Table-Cell--SpecsAttribute'>
            {t('Umpack')}
            <Tooltip
              isApplied={true}
              content={
                <div dangerouslySetInnerHTML={{
                  __html: t('Bulk packaging is an outer container comprising multiple sales units that is easy to store and move.')
                    + '<br/>' + t('Add {containerQuantitySuggestion} items to basket if you want to receive them in bulk packaging.', { containerQuantitySuggestion })
                    + t('Please note that packaging delivery is not always guaranteed.'),
                }} />
              }
              contentClassName='ProductSpecification-TooltipContent'
            >
              <Icon
                name={ICON_NAME.infoOutline}
                size={ICON_SIZE.small}
                className='ProductSpecification-TooltipIcon'
              />
            </Tooltip>
          </td>
          <td>
            <ul>
              <li>
                {containerQuantitySuggestion > 1 ? text.umpackPlural : text.umpack}
              </li>
            </ul>
          </td>
        </tr>
      </Fragment>,
    );
  }

  return (
    <div className='ProductSpecification'>
      <table className='cds-Table cds-Table--Specs'>
        <tbody>{attributeGroups}</tbody>
      </table>
    </div>
  );
};
